import { useEffect, useMemo, useState } from 'react';
import {
  Modal, TextInput, Textarea, NumberInput, Stack, Group, Button, Alert, Paper, Table, Text, Title, Loader, Center, Anchor,
} from '@mantine/core';
import { IconAdd as IconPlus } from '../../components/icons/CncIcons';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import ImageUpload from '../../components/ImageUpload';
import MarkupSlider from '../../components/MarkupSlider';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import AccessoryDistributorModal, { type AccessoryDistributor } from './AccessoryDistributorModal';

interface AccessoryFull {
  id: number;
  name: string;
  manufacturer: string | null;
  model: string | null;
  capabilities: string | null;
  notes: string | null;
  imagePath: string | null;
  powerKwh: number | null;
  valueEur: number | null;
  serviceLifeHours: number | null;
  markupPct: number;
  depreciationPerHour: number | null;
  hourlyRateEur: number | null;
  distributors: AccessoryDistributor[];
}

export default function AccessoryModal({
  id,
  onClose,
  onSaved,
  onDeleted,
}: {
  id: number | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const [loading, setLoading] = useState(!!id);
  const [data, setData] = useState<AccessoryFull | null>(null);

  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [notes, setNotes] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [powerKwh, setPowerKwh] = useState<number | string>('');
  const [valueEur, setValueEur] = useState<number | string>('');
  const [serviceLifeHours, setServiceLifeHours] = useState<number | string>('');
  const [markup, setMarkup] = useState(30);

  const [distributors, setDistributors] = useState<AccessoryDistributor[]>([]);
  const [editingDist, setEditingDist] = useState<AccessoryDistributor | 'new' | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const full = await api.get<AccessoryFull>(`/accessories/${id}`);
        setData(full);
        setName(full.name);
        setManufacturer(full.manufacturer ?? '');
        setModel(full.model ?? '');
        setCapabilities(full.capabilities ?? '');
        setNotes(full.notes ?? '');
        setImage(full.imagePath);
        setPowerKwh(full.powerKwh ?? '');
        setValueEur(full.valueEur ?? '');
        setServiceLifeHours(full.serviceLifeHours ?? '');
        setMarkup(full.markupPct);
        setDistributors(full.distributors);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const { depreciation, hourly } = useMemo(() => {
    const v = Number(valueEur); const h = Number(serviceLifeHours);
    const dep = (v && h) ? v / h : null;
    const p = Number(powerKwh) || 0;
    const base = (dep ?? 0) + p;
    const hr = (base > 0) ? base * (1 + markup / 100) : null;
    return { depreciation: dep, hourly: hr };
  }, [powerKwh, valueEur, serviceLifeHours, markup]);

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        manufacturer: manufacturer.trim() || null,
        model: model.trim() || null,
        capabilities: capabilities.trim() || null,
        notes: notes.trim() || null,
        imagePath: image,
        powerKwh: powerKwh === '' ? null : Number(powerKwh),
        valueEur: valueEur === '' ? null : Number(valueEur),
        serviceLifeHours: serviceLifeHours === '' ? null : Number(serviceLifeHours),
        markupPct: markup,
      };
      if (id) await api.put(`/accessories/${id}`, body);
      else await api.post('/accessories', body);
      notifications.show({ message: 'Accessory saved.', color: 'green' });
      onSaved();
    } catch (e: any) { setErr(e.message ?? 'Save failed'); }
    finally { setBusy(false); }
  }

  function confirmDelete() {
    if (!id) return;
    modals.openConfirmModal({
      title: `Delete ${data?.name}?`,
      children: 'This cannot be undone. The accessory will be detached from any equipment it is linked to.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/accessories/${id}`);
          notifications.show({ message: 'Accessory deleted.', color: 'gray' });
          onDeleted();
        } catch (e: any) { notifications.show({ message: e.message ?? 'Delete failed', color: 'red' }); }
      },
    });
  }

  async function refreshDistributors() {
    if (!id) return;
    const full = await api.get<AccessoryFull>(`/accessories/${id}`);
    setDistributors(full.distributors);
  }

  return (
    <Modal opened onClose={onClose} size="xl" title={<span className="modal-title-custom">{id ? `Edit ${data?.name ?? 'Accessory'}` : 'Add Accessory'}</span>}>
      {loading ? (
        <Center p="xl"><Loader /></Center>
      ) : (
        <Stack>
          <ImageUpload entity="accessories" value={image} onChange={setImage} />
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required maxLength={120} disabled={!writable} data-tour="accessory-name" />
          <Group grow>
            <TextInput label="Manufacturer" value={manufacturer} onChange={(e) => setManufacturer(e.currentTarget.value)} maxLength={120} disabled={!writable} />
            <TextInput label="Model" value={model} onChange={(e) => setModel(e.currentTarget.value)} maxLength={120} disabled={!writable} />
          </Group>
          <Textarea label="Capabilities" value={capabilities} onChange={(e) => setCapabilities(e.currentTarget.value)} maxLength={500} autosize minRows={2} description={`${capabilities.length}/500`} disabled={!writable} />
          <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} maxLength={500} autosize minRows={2} description={`${notes.length}/500`} disabled={!writable} />

          <div className="section">
            <div className="section-header">Running Costs</div>
            <div className="section-body">
              <Stack>
                <Group grow>
                  <NumberInput label="Power (kWh)" value={powerKwh} onChange={setPowerKwh} min={0.1} step={0.1} decimalScale={2} disabled={!writable} />
                  <NumberInput label="Value (EUR)" value={valueEur} onChange={setValueEur} min={1} step={10} decimalScale={2} disabled={!writable} />
                  <NumberInput
                    label="Service life (hours)"
                    description="Tip: years × 250 × 8"
                    value={serviceLifeHours}
                    onChange={setServiceLifeHours}
                    min={1}
                    step={100}
                    disabled={!writable}
                  />
                </Group>
                <MarkupSlider value={markup} onChange={setMarkup} disabled={!writable} />
                <Group grow>
                  <Paper withBorder p="xs">
                    <Text size="xs" c="dimmed">Depreciation / hour</Text>
                    <Text fw={600}>{depreciation != null ? `€ ${depreciation.toFixed(2)}` : '—'}</Text>
                  </Paper>
                  <Paper withBorder p="xs">
                    <Text size="xs" c="dimmed">Hourly rate</Text>
                    <Text fw={600}>{hourly != null ? `€ ${hourly.toFixed(2)}` : '—'}</Text>
                  </Paper>
                </Group>
              </Stack>
            </div>
          </div>

          <Title order={5} mt="md">Distributors</Title>
          {!id ? (
            <Text c="dimmed" size="sm" className="section-stub">
              Save the accessory first; then you can add distributors.
            </Text>
          ) : (
            <Paper withBorder>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Link</Table.Th>
                    <Table.Th>Contact</Table.Th>
                    <Table.Th>Price</Table.Th>
                    <Table.Th>Added</Table.Th>
                    <Table.Th>By</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {distributors.map((d) => (
                    <Table.Tr key={d.id} className="click-row" onClick={() => setEditingDist(d)}>
                      <Table.Td>{d.name}</Table.Td>
                      <Table.Td>{d.link ? <Anchor href={d.link} target="_blank" onClick={(e) => e.stopPropagation()}>Open</Anchor> : '—'}</Table.Td>
                      <Table.Td>{d.contact ?? '—'}</Table.Td>
                      <Table.Td>{d.priceEur != null ? `€ ${d.priceEur.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>{new Date(d.addedAt).toLocaleString()}</Table.Td>
                      <Table.Td>{d.addedByUsername ?? '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingDist('new')} disabled={!writable}>
                        Add Distributor
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {err && <Alert color="red">{err}</Alert>}
          <Group justify="space-between" mt="md">
            <div>
              {id && writable && <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>}
            </div>
            <Group>
              <Button variant="default" onClick={onClose}>Close</Button>
              {writable && <Button onClick={save} loading={busy} data-tour="accessory-save">Save</Button>}
            </Group>
          </Group>

          {id && editingDist !== null && (
            <AccessoryDistributorModal
              accessoryId={id}
              initial={editingDist === 'new' ? null : editingDist}
              onClose={() => setEditingDist(null)}
              onDone={async () => { await refreshDistributors(); setEditingDist(null); }}
            />
          )}
        </Stack>
      )}
    </Modal>
  );
}
